import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { BillingPlan, BillingPlanName, BILLING_INCLUDED_FEATURES, formatMoney, PlanSelection } from '../billing.models';

/** full = 1a (with the included list), blocked = 1b, compact = the 1f pop-ups. */
export type PlanPickerVariant = 'full' | 'blocked' | 'compact';

interface PlanPickerView {
    monthlyPrice: string;
    yearlyPrice: string;
    yearlyNote: string;
    saveTag: string;
    hint: string;
    canDecrease: boolean;
    canIncrease: boolean;
    totalLabel: string;
    total: string;
    stepIconSize: number;
}

/**
 * The plan cards, People stepper and total shared by the upgrade pop-up (1a), the trial-ended pop-up (1b) and the 1f pop-ups
 * ("Upgrade and Trial Ended.dc.html"). Yearly is selected by default; people start at the team size and stay between it and
 * the maximum. The caller projects its buttons under the total.
 */
@Component({
    selector: 'vf-plan-picker',
    templateUrl: './plan-picker.component.html',
    styleUrls: ['./plan-picker.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanPickerComponent implements OnChanges {
    @Input() plans: BillingPlan[] = [];
    @Input() teamSize = 1;
    @Input() maxPeople = 200;
    @Input() yearlySavingsPerPerson: number | null = null;
    @Input() variant: PlanPickerVariant = 'full';
    /** The card selected first (Change plan starts on the current plan); null = Yearly. */
    @Input() initialPlan: BillingPlanName | null = null;
    /** Change plan: people follow the team, so the stepper is hidden and this count is used. */
    @Input() fixedPeople: number | null = null;
    @Output() selectionChange = new EventEmitter<PlanSelection>();

    readonly features = BILLING_INCLUDED_FEATURES;
    plan: BillingPlanName = 'yearly';
    people = 1;
    view: PlanPickerView = PlanPickerComponent.emptyView();

    private monthly: BillingPlan | null = null;
    private yearly: BillingPlan | null = null;
    private initialPlanApplied = false;

    ngOnChanges(): void {
        this.monthly = this.plans?.find(p => p.plan === 'monthly') ?? null;
        this.yearly = this.plans?.find(p => p.plan === 'yearly') ?? null;
        if (this.initialPlan && !this.initialPlanApplied) {
            this.plan = this.initialPlan;
            this.initialPlanApplied = true;
        }
        this.people = this.fixedPeople !== null
            ? this.fixedPeople
            : Math.min(Math.max(this.people, this.minPeople), this.maxPeople);
        this.refresh();
    }

    selectPlan(plan: BillingPlanName): void {
        this.plan = plan;
        this.refresh();
    }

    decrease(): void {
        if (this.people > this.minPeople) {
            this.people--;
            this.refresh();
        }
    }

    increase(): void {
        if (this.people < this.maxPeople) {
            this.people++;
            this.refresh();
        }
    }

    trackByFeature(_index: number, feature: string): string {
        return feature;
    }

    private get minPeople(): number {
        return Math.max(1, this.teamSize || 1);
    }

    private refresh(): void {
        const currency = this.monthly?.currency ?? this.yearly?.currency ?? 'usd';
        const selected = this.plan === 'yearly' ? this.yearly : this.monthly;
        const persons = this.people === 1 ? 'person' : 'people';
        const savings = this.yearlySavingsPerPerson ?? 0;

        this.view = {
            monthlyPrice: this.monthly ? formatMoney(this.monthly.unitAmount, currency) : '',
            yearlyPrice: this.yearly ? formatMoney(this.yearly.monthlyEquivalent, currency) : '',
            yearlyNote: this.yearly ? `${formatMoney(this.yearly.unitAmount, currency)} per person, billed yearly` : '',
            saveTag: savings > 0 ? `Save ${formatMoney(savings, currency)}` : '',
            hint: `Can’t go below your current team size (${this.minPeople})`,
            canDecrease: this.people > this.minPeople,
            canIncrease: this.people < this.maxPeople,
            totalLabel: selected
                ? `${this.people} ${persons} × ${formatMoney(selected.unitAmount, currency)}, billed ${this.plan === 'yearly' ? 'yearly' : 'monthly'}`
                : '',
            total: selected
                ? `${formatMoney(selected.unitAmount * this.people, currency)} / ${this.plan === 'yearly' ? 'year' : 'month'}`
                : '',
            stepIconSize: this.variant === 'compact' ? 13 : 14,
        };
        this.selectionChange.emit({ plan: this.plan, people: this.people });
    }

    private static emptyView(): PlanPickerView {
        return {
            monthlyPrice: '',
            yearlyPrice: '',
            yearlyNote: '',
            saveTag: '',
            hint: '',
            canDecrease: false,
            canIncrease: false,
            totalLabel: '',
            total: '',
            stepIconSize: 14,
        };
    }
}
